/**
 * Device notifications — Web Push helpers.
 *
 * Browser/OS permission still requires a user gesture. Once permission
 * has been granted, FrontRow treats device notifications as ON by
 * default unless the parent explicitly turns them off in Settings.
 */
import axios from "axios";
import {
  isNativePushSupported, readNativeNotificationState,
  enableNativeNotifications, detachNativeNotifications,
} from "./nativePushNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUSH_PREF_KEY = "frontrow.deviceNotifications.enabled";

export function isPushSupported() {
  if (isNativePushSupported()) return true;
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
}

export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function sameApplicationServerKey(subscription, expectedKey) {
  const currentKey = subscription?.options?.applicationServerKey;
  if (!currentKey) return false;
  const current = new Uint8Array(currentKey);
  if (current.length !== expectedKey.length) return false;
  for (let i = 0; i < current.length; i += 1) {
    if (current[i] !== expectedKey[i]) return false;
  }
  return true;
}

function readProductPreference() {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(PUSH_PREF_KEY) !== "off";
  } catch {
    return true;
  }
}

function writeProductPreference(enabled) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PUSH_PREF_KEY, enabled ? "on" : "off");
  } catch {
    /* localStorage may be unavailable; browser permission still governs */
  }
}

async function getRegistration() {
  try {
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) return existing;

    // A deploy can retire an old FrontRow worker and register /sw.js moments
    // later. Auth may become ready in between those two events. Waiting for
    // serviceWorker.ready here closes that race so a device with permission
    // already granted automatically recreates its PushSubscription instead of
    // silently staying unsubscribed until the parent happens to open Settings.
    return (await navigator.serviceWorker.ready) || null;
  } catch {
    return null;
  }
}

async function subscribeGrantedDevice() {
  const reg = await getRegistration();
  if (!reg) return { status: "no_service_worker" };
  try {
    const cfg = await axios.get(`${API}/push/config`);
    const key = cfg?.data?.vapid_public_key;
    if (!key) return { status: "not_configured" };
    const expectedKey = urlBase64ToUint8Array(key);
    let sub = await reg.pushManager.getSubscription();

    // A PushSubscription is bound to the VAPID application-server key it was
    // created with. If FrontRow's deploy-time VAPID key has rotated, silently
    // replace the stale subscription after permission has already been granted.
    // This does not call Notification.requestPermission().
    if (sub && !sameApplicationServerKey(sub, expectedKey)) {
      const staleEndpoint = sub.endpoint;
      try {
        await axios.post(`${API}/push/unsubscribe`, { endpoint: staleEndpoint });
      } catch {
        /* backend also prunes dead/stale endpoints during send */
      }
      try {
        await sub.unsubscribe();
      } catch {
        /* continue and attempt a fresh subscription below */
      }
      sub = null;
    }

    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: expectedKey,
      });
    }
    await axios.post(`${API}/push/subscribe`, sub.toJSON());
    return { status: "granted" };
  } catch {
    return { status: "error" };
  }
}

export async function readDeviceNotificationState() {
  if (isNativePushSupported()) return readNativeNotificationState(readProductPreference());
  if (!isPushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  if (!readProductPreference()) return "off";
  try {
    const reg = await getRegistration();
    if (!reg) return "off";
    const sub = await reg.pushManager.getSubscription();
    return sub && Notification.permission === "granted" ? "on" : "off";
  } catch {
    return "off";
  }
}

/**
 * Non-prompting default-on path. If OS/browser permission has already
 * been granted and the parent has not opted out, ensure the device is
 * subscribed. Never calls Notification.requestPermission().
 */
export async function ensureDeviceNotifications() {
  if (isNativePushSupported()) {
    return readProductPreference() ? enableNativeNotifications(false) : { status: "off" };
  }
  if (!isPushSupported()) return { status: "unsupported" };
  if (!readProductPreference()) return { status: "off" };
  if (Notification.permission !== "granted") {
    return {
      status: Notification.permission === "denied" ? "denied" : "permission_required",
    };
  }
  return subscribeGrantedDevice();
}

/**
 * User-gesture path. The browser/OS permission prompt cannot be skipped.
 * Once granted, FrontRow records the product preference as ON.
 */
export async function enableDeviceNotifications() {
  if (isNativePushSupported()) {
    const result = await enableNativeNotifications(true);
    if (result.status === "granted") writeProductPreference(true);
    return result;
  }
  if (!isPushSupported()) return { status: "unsupported" };
  let perm;
  try {
    perm = await Notification.requestPermission();
  } catch {
    return { status: "unsupported" };
  }
  if (perm !== "granted") {
    return { status: perm === "denied" ? "denied" : "default" };
  }
  writeProductPreference(true);
  return subscribeGrantedDevice();
}

export async function disableDeviceNotifications() {
  if (isNativePushSupported()) {
    await detachNativeNotifications();
    writeProductPreference(false);
    return { status: "off" };
  }
  writeProductPreference(false);
  if (!isPushSupported()) return { status: "unsupported" };
  try {
    const reg = await getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      try {
        await axios.post(`${API}/push/unsubscribe`, {
          endpoint: sub.endpoint,
        });
      } catch {
        /* server prunes dead endpoints on next send */
      }
      try {
        await sub.unsubscribe();
      } catch {
        /* already gone */
      }
    }
  } catch {
    /* explicit product opt-out is already persisted locally */
  }
  return { status: "off" };
}
